import { Link, useParams } from "wouter";
import { GuideLinks } from "@/components/GuideLinks";
import { getPersonaById } from "@/lib/personas-data";
import { getPersonaContent } from "@/lib/persona-content";
import { getToolById } from "@/lib/tools-data";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useStructuredData } from "@/hooks/useStructuredData";
import { useLocale } from "@/hooks/useLocale";
import { UsageAndUpgradeStrip } from "@/components/UsageAndUpgradeStrip";
import { splitWorkflowTools, workflowPriceLine } from "@/lib/workflow/productPrice";

export default function SolutionPage() {
  const { id } = useParams<{ id: string }>();
  const { localizedHref } = useLocale();
  const persona = getPersonaById(id || "");
  const personaContent = persona ? getPersonaContent(persona.id) : undefined;

  usePageMeta({
    title: persona ? `Tools for ${persona.title}` : "Solution Not Found",
    description: persona ? persona.description : undefined,
    path: `/solutions/${id ?? ""}`,
    noindex: !persona,
  });

  useStructuredData(
    persona
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.freedailypro.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Solutions",
              item: "https://www.freedailypro.com/solutions",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: persona.title,
              item: `https://www.freedailypro.com/solutions/${persona.id}`,
            },
          ],
        }
      : null
  );

  if (!persona) {
    return (
      <div className="container py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Solution Not Found</h1>
        <Link href={localizedHref("/solutions")}>
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Solutions
          </Button>
        </Link>
      </div>
    );
  }

  const allTools = persona.toolIds.map(tid => getToolById(tid)).filter((t): t is NonNullable<typeof t> => Boolean(t));
  // A Workflow Product has its own monthly plan, so it is shown apart from the trial-use banner and the single tools.
  const { workflow: workflowTools, single: tools } = splitWorkflowTools(allTools);

  return (
    <div className="page-enter">
      <section className="border-b border-border/50 bg-muted/20">
        <div className="container max-w-4xl mx-auto py-10 px-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link href="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link href={localizedHref("/solutions")} className="hover:text-foreground transition-colors">
              For Your Work
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">{persona.title}</span>
          </div>
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${persona.color}15` }}
            >
              <persona.icon className="h-6 w-6" style={{ color: persona.color }} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Tools for {persona.title}</h1>
              <p className="text-muted-foreground mt-1">{persona.description}</p>
            </div>
          </div>
        </div>
      </section>

      {personaContent && (
        <section className="container max-w-4xl mx-auto pt-8 px-4">
          <p className="text-sm text-foreground/90 leading-relaxed">{personaContent}</p>
        </section>
      )}

      {workflowTools.length > 0 && (
        <section className="container max-w-4xl mx-auto pt-8 px-4" aria-labelledby="sol-workflow-h" data-testid="sol-workflow">
          <h2 id="sol-workflow-h" className="text-sm font-semibold mb-2">Workflow product for this job</h2>
          <div className="grid gap-4">
            {workflowTools.map(tool => (
              <Link key={tool.id} href={localizedHref(`/tool/${tool.id}`)}>
                <div className="group p-5 rounded-xl border-2 border-primary/40 bg-card hover:border-primary cursor-pointer flex flex-col sm:flex-row sm:items-center gap-3 transition-colors">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base group-hover:text-primary transition-colors">{tool.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{tool.description}</p>
                  </div>
                  <div className="shrink-0 sm:text-right">
                    <p className="text-sm font-semibold" data-testid="sol-workflow-price">{workflowPriceLine(tool.id)}</p>
                    <p className="text-xs text-muted-foreground">Its own plan. Not covered by the daily trial uses or the day pass below.</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="container max-w-4xl mx-auto pt-8 px-4">
        {workflowTools.length > 0 && <h2 className="text-sm font-semibold mb-2">Single tools</h2>}
        <UsageAndUpgradeStrip />
      </section>

      <section className="container max-w-4xl mx-auto py-10 px-4">
        <div className="grid sm:grid-cols-2 gap-4">
          {tools.map(tool => (
            <Link key={tool.id} href={localizedHref(`/tool/${tool.id}`)}>
              <div className="tool-card group p-5 rounded-xl border border-border/60 bg-card hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 cursor-pointer h-full flex flex-col transition-all duration-200">
                <h3 className="font-semibold text-base mb-2 group-hover:text-primary transition-colors">{tool.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{tool.description}</p>
                <div className="mt-3 flex items-center text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:translate-x-0.5">
                  Open Tool <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <GuideLinks kind="personas" id={persona.id} />
    </div>
  );
}
